import Add from '@mui/icons-material/Add';
import AssistantIcon from '@mui/icons-material/Assistant';
import Close from '@mui/icons-material/Close';
import CloseFullscreen from '@mui/icons-material/CloseFullscreen';
import LayersOutlined from '@mui/icons-material/LayersOutlined';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { ReactNode, useEffect, useLayoutEffect, useState } from 'react';
import { useLocalStorage } from 'usehooks-ts';

import ToggleSidebarButton from '~components/Buttons/ToggleSidebarButton';
import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { TreadAgentSkillBasic } from '~hooks/useTreadAgent/models';
import { SearchInput } from '~pages/Dispatch/components/drivers/Filters/SearchInput';
import useTreadAgentConversationsQuery from '~queries/useTreadAgentConversationsQuery';
import useTreadAgentSkillsQuery from '~queries/useTreadAgentSkillsQuery';
import { useStores } from '~store';
import TreadAgentConversationList from '~tread-agent/TreadAgentConversationList';
import TreadAgentSkillPane from '~tread-agent/TreadAgentSkillPane';
import TreadAgentSkillsPage from '~tread-agent/TreadAgentSkillsPage';
import { ExpandedTabStripRenderer } from '~tread-agent/TreadAgentTabs';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

// The left conversation pane; wide enough for a full title line without crowding the chat.
const CONVERSATION_PANE_WIDTH_IN_PX = 300;
// Cap the chat column at a comfortable reading measure so the transcript and composer don't
// stretch edge-to-edge on wide monitors; it stays centered in the remaining space.
export const CHAT_CONTENT_MAX_WIDTH_IN_PX = 860;
// Fallback distance from the top of the viewport to the content region while the real offset
// is measured — the global nav row height (GLOBAL_NAV_ROW_HEIGHT_PX in AppHeader).
const DEFAULT_CONTENT_TOP_OFFSET_IN_PX = 64;
const FRESH_CHAT_MAX_WIDTH_IN_PX = 640;
const BUILD_SKILL_NAME = 'build-skill';
const SIDEBAR_COLLAPSED_STORAGE_KEY = 'tread.tread_agent.sidebar_collapsed';

// Measures the top of the routed page content (<main>) so the overlay sits directly below the
// nav and any billing banner, regardless of the banner's presence or height. Clamped to 0 so a
// scrolled-away nav (the header is position: static) leaves the overlay filling from the top.
function useContentTopOffset() {
  const [topOffset, setTopOffset] = useState(DEFAULT_CONTENT_TOP_OFFSET_IN_PX);

  useLayoutEffect(() => {
    const measure = () => {
      const mainElement = document.querySelector('main');

      if (mainElement) {
        setTopOffset(Math.max(0, mainElement.getBoundingClientRect().top));
      }
    };

    measure();
    window.addEventListener('resize', measure);

    return () => window.removeEventListener('resize', measure);
  }, []);

  return topOffset;
}

type SkillsView =
  | { kind: 'list' }
  | { kind: 'detail'; skill: TreadAgentSkillBasic | null };

interface TreadAgentExpandedViewProps {
  activeConversationId: string | null;
  // The chat composer, owned by the panel so its draft survives the compact/expanded toggle.
  chatInput: ReactNode;
  isFreshChat: boolean;
  // Collapse back to the compact docked panel (keeps the conversation open).
  onCollapse: () => void;
  onClose: () => void;
  onNewChat: () => void;
  onSelectConversation: (conversationId: string) => void;
  onUseSkill: (skillName: string) => void;
  // The transcript, owned by the panel (reads the active conversation from the store).
  messageList: ReactNode;
  tabStrip: ExpandedTabStripRenderer;
}

const TreadAgentExpandedView = observer(
  ({
    activeConversationId,
    chatInput,
    isFreshChat,
    onCollapse,
    onClose,
    onNewChat,
    onSelectConversation,
    onUseSkill,
    messageList,
    tabStrip,
  }: TreadAgentExpandedViewProps) => {
    const theme = useTheme();
    const { userStore } = useStores();
    const firstName = userStore.user?.firstName;
    const companyId = getEffectiveUserCompanyId(userStore);
    const contentTopOffset = useContentTopOffset();
    const [search, setSearch] = useState('');
    const [skillsView, setSkillsView] = useState<SkillsView | null>(null);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useLocalStorage(
      SIDEBAR_COLLAPSED_STORAGE_KEY,
      false,
    );

    // The expanded view always shows the full history, so fetch it as soon as it opens.
    const { conversations, isFetching } = useTreadAgentConversationsQuery(companyId);
    const { isLoading: isLoadingSkills, skills } = useTreadAgentSkillsQuery(companyId);
    const { canCreateTreadAgentSkill, canDeleteTreadAgentSkill, canEditTreadAgentSkill } =
      userStore.getPermissions();

    const handleNewChat = () => {
      setSkillsView(null);
      onNewChat();
    };

    const handleSelectConversation = (conversationId: string) => {
      setSkillsView(null);
      onSelectConversation(conversationId);
    };

    const handleUseSkill = (skillName: string) => {
      setSkillsView(null);
      onUseSkill(skillName);
    };

    // Escape collapses back to the compact panel rather than closing outright, matching the
    // least-destructive expectation for a maximize/restore toggle.
    useEffect(() => {
      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          onCollapse();
        }
      };

      window.addEventListener('keydown', handleKeyDown);

      return () => window.removeEventListener('keydown', handleKeyDown);
    }, [onCollapse]);

    // Lock background scroll while the overlay is open. The nav is position: static, so a wheel
    // over the fixed scrim would otherwise scroll the page underneath — moving the nav out from
    // under the overlay and leaving the measured top inset (useContentTopOffset) stale.
    useEffect(() => {
      const { body } = document;
      const previousOverflow = body.style.overflow;
      body.style.overflow = 'hidden';

      return () => {
        body.style.overflow = previousOverflow;
      };
    }, []);

    const skillsContent =
      skillsView && companyId ? (
        skillsView.kind === 'list' ? (
          <TreadAgentSkillsPage
            canCreate={canCreateTreadAgentSkill}
            canDelete={canDeleteTreadAgentSkill}
            canEdit={canEditTreadAgentSkill}
            companyId={companyId}
            isLoading={isLoadingSkills}
            onCreate={() => setSkillsView({ kind: 'detail', skill: null })}
            onCreateWithAgent={() => handleUseSkill(BUILD_SKILL_NAME)}
            onOpen={(skill) => setSkillsView({ kind: 'detail', skill })}
            onUse={(skill) => handleUseSkill(skill.name)}
            skills={skills}
          />
        ) : (
          <TreadAgentSkillPane
            canCreate={canCreateTreadAgentSkill}
            canDelete={canDeleteTreadAgentSkill}
            canEdit={canEditTreadAgentSkill}
            companyId={companyId}
            onBack={() => setSkillsView({ kind: 'list' })}
            onUse={handleUseSkill}
            skill={skillsView.skill}
          />
        )
      ) : null;

    return (
      <Box
        sx={{
          // A translucent scrim over the content area (not the nav) to focus the overlay.
          // Clicking it is intentionally inert — only Escape or the header controls dismiss it.
          // Mode-aware so it dims the page rather than washing it white in dark mode.
          backgroundColor:
            theme.palette.mode === 'dark'
              ? 'rgba(10, 24, 32, 0.8)'
              : 'rgba(255, 255, 255, 0.8)',
          bottom: 0,
          display: 'flex',
          left: 0,
          // A margin on every side so the panel reads as a floating surface.
          p: 3,
          position: 'fixed',
          right: 0,
          top: `${contentTopOffset}px`,
          // Same layer as the compact panel: above page content, below blocking MUI dialogs.
          zIndex: theme.zIndex.modal - 1,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            border: `1px solid ${theme.brandV2.colors.treadGray7}`,
            borderRadius: theme.brandV2.borderRadius,
            boxShadow: '0 4px 24px rgba(0, 0, 0, 0.12)',
            display: 'flex',
            flexGrow: 1,
            overflow: 'hidden',
          }}
        >
          {!isSidebarCollapsed && (
            <Box
              sx={{
                borderRight: `1px solid ${theme.brandV2.colors.treadGray7}`,
                display: 'flex',
                flexDirection: 'column',
                flexShrink: 0,
                width: `${CONVERSATION_PANE_WIDTH_IN_PX}px`,
              }}
            >
              <Box
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  justifyContent: 'space-between',
                  px: 1.5,
                  py: 1.5,
                }}
              >
                <Box sx={{ alignItems: 'center', display: 'flex', gap: 1, minWidth: 0 }}>
                  <AssistantIcon sx={{ fontSize: 20 }} />

                  <Typography noWrap sx={{ fontSize: '15px', fontWeight: 600 }}>
                    {t('tread_agent.title')}
                  </Typography>
                </Box>

                <ToggleSidebarButton
                  isCollapsed={false}
                  onClick={() => setIsSidebarCollapsed(true)}
                />
              </Box>

              <Box sx={{ px: 1.5, pb: 1 }}>
                <SmallButton
                  color="brandV2Black"
                  fullWidth
                  onClick={handleNewChat}
                  startIcon={<Add sx={{ fontSize: 16 }} />}
                >
                  {t('tread_agent.new_chat')}
                </SmallButton>

                <SmallButton
                  color="brandV2Black"
                  fullWidth
                  onClick={() => setSkillsView({ kind: 'list' })}
                  startIcon={<LayersOutlined sx={{ fontSize: 16 }} />}
                  sx={{
                    backgroundColor: skillsView
                      ? theme.brandV2.colors.treadGray8
                      : undefined,
                    borderColor: 'divider',
                    color: 'text.primary',
                    mt: 0.75,
                  }}
                  variant="outlined"
                >
                  {t('tread_agent.skills.title')}
                </SmallButton>
              </Box>

              <Divider />

              <Box sx={{ px: 1.5, py: 1 }}>
                <SearchInput
                  onChange={setSearch}
                  placeholder={t('tread_agent.search_placeholder')}
                  value={search}
                />
              </Box>

              <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
                <TreadAgentConversationList
                  activeConversationId={skillsView ? null : activeConversationId}
                  conversations={conversations}
                  isFetching={isFetching}
                  onSelect={handleSelectConversation}
                  search={search}
                />
              </Box>
            </Box>
          )}

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flexGrow: 1,
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                alignItems: 'center',
                borderBottom: `solid 1px ${theme.brandV2.colors.treadGray7}`,
                display: 'flex',
                gap: 0.5,
                px: 2,
                py: 1,
              }}
            >
              {isSidebarCollapsed && (
                <ToggleSidebarButton
                  isCollapsed
                  onClick={() => setIsSidebarCollapsed(false)}
                  sx={{ ml: -0.5, mr: 1 }}
                />
              )}

              {tabStrip({
                isSkillsViewOpen: skillsView !== null,
                onLeaveSkillsView: () => setSkillsView(null),
              })}

              <SmallButton
                color="brandV2Black"
                onClick={handleNewChat}
                startIcon={<Add sx={{ fontSize: 16 }} />}
                sx={{
                  borderColor: 'divider',
                  color: 'text.primary',
                  flexShrink: 0,
                  '&.MuiButtonBase-root': { px: 1 },
                  '& .MuiButton-startIcon': { ml: 0, mr: 0.25 },
                }}
                variant="outlined"
              >
                {t('tread_agent.new')}
              </SmallButton>

              <IconButton
                aria-label={t('tread_agent.collapse')}
                onClick={onCollapse}
                size="small"
              >
                <CloseFullscreen sx={{ fontSize: 18 }} />
              </IconButton>

              <IconButton aria-label={t('actions.close')} onClick={onClose} size="small">
                <Close sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>

            {skillsContent ??
              (isFreshChat ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flexGrow: 1,
                    overflowY: 'auto',
                    px: 3,
                    py: 6,
                  }}
                >
                  <Box
                    sx={{
                      maxWidth: `${FRESH_CHAT_MAX_WIDTH_IN_PX}px`,
                      mx: 'auto',
                      my: 'auto',
                      width: '100%',
                    }}
                  >
                    <Box sx={{ mb: 3, px: 1.5 }}>
                      {firstName && (
                        <Typography
                          sx={{ color: 'text.secondary', fontWeight: 400 }}
                          variant="h6"
                        >
                          {t('tread_agent.greeting', { name: firstName })}
                        </Typography>
                      )}

                      <Typography sx={{ fontWeight: 400 }} variant="h6">
                        {t('tread_agent.help_prompt')}
                      </Typography>
                    </Box>

                    {chatInput}

                    {messageList}
                  </Box>
                </Box>
              ) : (
                <>
                  {messageList}

                  <Box
                    sx={{
                      maxWidth: `${CHAT_CONTENT_MAX_WIDTH_IN_PX}px`,
                      mx: 'auto',
                      width: '100%',
                    }}
                  >
                    {chatInput}
                  </Box>
                </>
              ))}
          </Box>
        </Paper>
      </Box>
    );
  },
);

export default TreadAgentExpandedView;
